import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { APP_ENV } from '../tokens/app-environment.token';
import { formatMaintenanceNotice } from './maintenance-notice.format';
import type { MaintenanceLockdownDisplay, MaintenanceNotice } from './maintenance.models';
import { NoticesApiService } from './notices.api.service';
import type { NoticeDto } from './notices.models';
import { isMaintenanceWindowActive, isValidMaintenanceNotice } from './maintenance-window';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const CLOCK_TICK_INTERVAL_MS = 60 * 1000;

@Injectable({ providedIn: 'root' })
export class MaintenanceNoticeService {
  private readonly document = inject(DOCUMENT);
  private readonly env = inject(APP_ENV);
  private readonly authService = inject(AuthService);
  private readonly noticesApi = inject(NoticesApiService);
  private readonly msal = inject(MsalService, { optional: true });

  private monitoringStarted = false;

  private readonly notice = signal<MaintenanceNotice | null>(null);
  private readonly dismissedNoticeId = signal<string | null>(null);
  private readonly now = signal(Date.now());

  readonly banner = computed(() => {
    const notice = this.notice();
    if (!notice || notice.id === this.dismissedNoticeId()) {
      return null;
    }

    const currentTime = new Date(this.now());
    const endAt = new Date(notice.endAt);
    if (Number.isNaN(endAt.getTime()) || currentTime.getTime() >= endAt.getTime()) {
      return null;
    }

    return formatMaintenanceNotice(notice, currentTime);
  });

  readonly dismissible = computed(() => this.notice()?.dismissible ?? false);

  readonly lockdownPending = computed(() => {
    if (!this.authService.isEnabled || !this.enforcedLockdownNotice()) {
      return false;
    }

    return !this.authService.rolesResolved();
  });

  readonly lockdown = computed((): MaintenanceLockdownDisplay | null => {
    const notice = this.enforcedLockdownNotice();
    if (!notice || !this.authService.isEnabled) {
      return null;
    }

    if (!this.authService.rolesResolved()) {
      return null;
    }

    if (this.authService.isDeveloper()) {
      return null;
    }

    const schedule = formatMaintenanceNotice(notice, new Date(this.now()));
    return {
      title: notice.title,
      message: notice.message,
      headline: schedule.headline,
      details: schedule.details,
      moreInfoUrl: notice.moreInfoUrl,
    };
  });

  readonly isLockedOut = computed(() => this.lockdownPending() || this.lockdown() !== null);

  startMonitoring(): void {
    const window = this.document.defaultView;
    if (this.monitoringStarted || !window) {
      return;
    }
    this.monitoringStarted = true;

    // Avoid hitting /api before MSAL has a session — that triggers MsalInterceptor
    // loginRedirect and races with MsalGuard. Unauthenticated users redirect away;
    // on return, APP_INITIALIZER establishes the session before this runs again.
    if (!this.authEnabled || this.hasAuthenticatedSession()) {
      this.beginPolling(window);
    }
  }

  dismiss(): void {
    const notice = this.notice();
    if (!notice?.dismissible) {
      return;
    }

    this.dismissedNoticeId.set(notice.id);
  }

  async refreshNotice(): Promise<void> {
    try {
      const response = await firstValueFrom(this.noticesApi.listNotices());
      const maintenanceNotice = selectMaintenanceNotice(response.notices);
      if (!maintenanceNotice || !isValidMaintenanceNotice(maintenanceNotice)) {
        this.notice.set(null);
        return;
      }

      this.notice.set(maintenanceNotice);
    } catch {
      /* offline or transient API failure — keep last known notice */
    }
  }

  private enforcedLockdownNotice(): MaintenanceNotice | null {
    const notice = this.notice();
    if (!notice?.blocksAppAccess) {
      return null;
    }

    if (!isMaintenanceWindowActive(notice, new Date(this.now()))) {
      return null;
    }

    return notice;
  }

  private get authEnabled(): boolean {
    return this.env.auth.enabled && !!this.env.auth.clientId;
  }

  private hasAuthenticatedSession(): boolean {
    if (!this.msal) {
      return false;
    }

    return (
      !!this.msal.instance.getActiveAccount() || this.msal.instance.getAllAccounts().length > 0
    );
  }

  private beginPolling(window: Window): void {
    void this.refreshNotice();
    window.setInterval(() => void this.refreshNotice(), POLL_INTERVAL_MS);
    window.setInterval(() => this.refreshNow(), CLOCK_TICK_INTERVAL_MS);
    window.addEventListener('visibilitychange', () => {
      if (this.document.visibilityState === 'visible') {
        void this.refreshNotice();
        this.refreshNow();
      }
    });
  }

  private refreshNow(): void {
    this.now.set(Date.now());
  }
}

function selectMaintenanceNotice(notices: readonly NoticeDto[]): MaintenanceNotice | null {
  const maintenance = notices.find((notice) => notice.kind === 'Maintenance');
  return maintenance ? toMaintenanceNotice(maintenance) : null;
}

function toMaintenanceNotice(notice: NoticeDto): MaintenanceNotice {
  return {
    id: notice.id,
    title: notice.title,
    message: notice.message,
    startAt: notice.startsAtUtc,
    endAt: notice.endsAtUtc,
    dismissible: notice.dismissible,
    blocksAppAccess: notice.blocksAppAccess ?? false,
    moreInfoUrl: notice.moreInfoUrl ?? null,
  };
}
